import React, { useState } from 'react';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function EmployeeTrainingTab({ employeeId, training, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  
  // Sorting
  const { items: sortedTraining, requestSort, getSortIndicator } = useSortableTable(training, { key: 'completion_date', direction: 'desc' });
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [formData, setFormData] = useState({
    training_name: '',
    training_type: 'Certification',
    provider: '',
    start_date: '',
    completion_date: '',
    expiry_date: '',
    certificate_number: '',
    pass_fail: '',
    score: '',
    cost: '',
    status: 'Active',
    document_path: '',
    notes: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setFormData({
      training_name: '',
      training_type: 'Certification',
      provider: '',
      start_date: '',
      completion_date: '',
      expiry_date: '',
      certificate_number: '',
      pass_fail: '',
      score: '',
      cost: '',
      status: 'Active',
      document_path: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      training_name: record.training_name || '',
      training_type: record.training_type || 'Certification',
      provider: record.provider || '',
      start_date: record.start_date ? record.start_date.split('T')[0] : '',
      completion_date: record.completion_date ? record.completion_date.split('T')[0] : '',
      expiry_date: record.expiry_date ? record.expiry_date.split('T')[0] : '',
      certificate_number: record.certificate_number || '',
      pass_fail: record.pass_fail || '',
      score: record.score || '',
      cost: record.cost || '',
      status: record.status || 'Active',
      document_path: record.document_path || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.training_name) {
      alert('Training name is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingRecord
        ? `/api/employees/training/${editingRecord.id}`
        : '/api/employees/training';

      const response = await apiFetch(url, {
        method: editingRecord ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          employee_id: employeeId
        })
      });

      if (response.ok) {
        setShowModal(false);
        onRefresh();
      } else {
        alert('Error saving training record');
      }
    } catch (error) {
      console.error('Error saving training:', error);
      alert('Error saving training record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, trainingName) => {
    if (!window.confirm(`Delete training "${trainingName}"?`)) return;

    try {
      await apiFetch(`/api/employees/training/${id}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting training:', error);
      alert('Error deleting training record');
    }
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Certificate must be less than 5MB');
      return;
    }
    
    setUploadingCert(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({...formData, document_path: event.target.result});
      setUploadingCert(false);
    };
    
    reader.readAsDataURL(file);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Training & Certifications</h2>
          <button 
            onClick={handleAdd}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
          >
            + Add Training
          </button>
        </div>

      {training.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No training records</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('training_name')}
                >
                  Training Name{getSortIndicator('training_name')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('training_type')}
                >
                  Type{getSortIndicator('training_type')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('provider')}
                >
                  Provider{getSortIndicator('provider')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('completion_date')}
                >
                  Completion Date{getSortIndicator('completion_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('expiry_date')}
                >
                  Expiry Date{getSortIndicator('expiry_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('status')}
                >
                  Status{getSortIndicator('status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTraining.map((record, idx) => (
                <tr key={record.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900">{record.training_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{record.training_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{record.provider || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.completion_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.expiry_date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      record.status === 'Active' ? 'bg-green-100 text-green-800' :
                      record.status === 'Expired' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(record)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(record.id, record.training_name)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Add/Edit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-600 text-white px-6 py-4 rounded-t-lg">
            <h3 className="text-xl font-semibold">
              {editingRecord ? 'Edit Training' : 'Add Training'}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Name *</label>
                <input
                  type="text"
                  value={formData.training_name}
                  onChange={(e) => setFormData({...formData, training_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Type</label>
                <select
                  value={formData.training_type}
                  onChange={(e) => setFormData({...formData, training_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="Certification">Certification</option>
                  <option value="Course">Course</option>
                  <option value="On-the-job">On-the-job</option>
                  <option value="Safety">Safety</option>
                  <option value="Technical">Technical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <input
                  type="text"
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Completion Date</label>
                <input
                  type="date"
                  value={formData.completion_date}
                  onChange={(e) => setFormData({...formData, completion_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Number</label>
                <input
                  type="text"
                  value={formData.certificate_number}
                  onChange={(e) => setFormData({...formData, certificate_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pass/Fail</label>
                <select
                  value={formData.pass_fail}
                  onChange={(e) => setFormData({...formData, pass_fail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="">-</option>
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Score</label>
                <input
                  type="text"
                  value={formData.score}
                  onChange={(e) => setFormData({...formData, score: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  placeholder="85%"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">£</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: e.target.value})}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                  <option value="Pending Renewal">Pending Renewal</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Image</label>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {formData.document_path ? (
                    <img 
                      src={formData.document_path} 
                      alt="Certificate" 
                      className="w-32 h-32 object-cover rounded border-2 border-gray-300"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <span className="text-gray-400 text-xs text-center">No certificate</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCertificateUpload}
                    disabled={uploadingCert}
                    className="text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF (max 5MB)</p>
                  {uploadingCert && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                  {formData.document_path && (
                    <button
                      onClick={() => setFormData({...formData, document_path: ''})}
                      className="text-xs text-red-600 hover:text-red-800 mt-2"
                    >
                      Remove certificate
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Add Training')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

export default EmployeeTrainingTab;
